import { CalendarView } from '@/domains/calendar/components/CalendarView';
import type {
  CalendarFilterValues,
  CalendarTeam,
} from '@/domains/calendar/components/CalendarView';
import { SCHEDULE_FILTER_DEFAULTS } from '@/domains/calendar/constants/scheduleFilterDefaults';
import type { Job } from '@/domains/jobs/components/JobCard';
import {
  useScheduleWorkOrder,
  useUnscheduleWorkOrder,
} from '@/domains/jobs/hooks/useWorkOrderActions';
import {
  useCreatePlannerEvent,
  usePlannerAreas,
  usePlannerJobCategories,
  usePlannerJobPriorities,
  usePlannerResources,
  useScheduledJobsInfinite,
  useUnscheduledJobs,
} from '@/domains/planner';
import { queryKeys } from '@/lib/query-client';
import { useUrlFilters } from '@/shared/hooks';
import { resolveAssigneeLabel } from '@/shared/lib/assignee-utils';
import { mapPriority } from '@/shared/lib/priority';
import { showInfoToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import type { PlannerJob } from '@skewb-works/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

export function mapPlannerJobToJob(pj: PlannerJob): Job {
  return {
    id: String(pj.id),
    title: pj.name ?? 'Untitled',
    description: pj.job_category_name ?? pj.description ?? '',
    status: 'pending',
    priority: mapPriority(pj.priority_name),
    // Passed through 1:1 — PriorityBadge owns the unrecognised-name fallback, so
    // filtering here would only drop the label twice (CWL-314).
    priorityName: pj.priority_name ?? undefined,
    location: [pj.address, pj.town, pj.pincode].filter(Boolean).join(', '),
    estimatedHours: pj.duration != null ? Math.round(pj.duration / 60) : 0,
    duration: pj.duration ?? null,
    tags: [pj.job_category_name, pj.client_name].filter(Boolean) as string[],
    client: pj.client_name ?? undefined,
    category: pj.job_category_name ?? undefined,
    coordinates: pj.coordinates ?? undefined,
    createdTime: pj.created_time ?? undefined,
    updatedTime: pj.updated_time ?? undefined,
    scheduledDate: pj.scheduled_date ?? undefined,
    scheduledEndDate: pj.scheduled_end_date ?? undefined,
    startTime: pj.schedule_start ?? undefined,
    endTime: pj.schedule_end ?? undefined,
    teamId: pj.team_id ? String(pj.team_id) : undefined,
    assignee: resolveAssigneeLabel(pj.team_name, pj.assigned_to_name),
    workOrderStatus: pj.work_order_status ?? undefined,
    permitRequired: pj.permit_required,
    reinstatementRequired: pj.reinstatement_required,
    permitStatusId: pj.permit_status_id ?? null,
    permitStatusName: pj.permit_status_name ?? null,
    permitReferenceNumber: pj.permit_reference_number ?? null,
    hasTasks: pj.has_tasks,
    isScheduled: pj.is_scheduled,
  };
}

/** Scheduled jobs fetched per page for the scheduler's "Load More" pagination. */
const SCHEDULED_PAGE_SIZE = 200;

export function SchedulePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: resources = [] } = usePlannerResources();
  const [urlFilters, setUrlFilters] = useUrlFilters(SCHEDULE_FILTER_DEFAULTS);

  const searchTerm = urlFilters.search;
  const filters: CalendarFilterValues = useMemo(
    () => ({ category: urlFilters.category }),
    [urlFilters.category],
  );
  const selectedTeams = useMemo(
    () => (urlFilters.teams ? urlFilters.teams.split(',').filter(Boolean) : []),
    [urlFilters.teams],
  );
  const selectedPriorities = useMemo(
    () => (urlFilters.priority ? urlFilters.priority.split(',').filter(Boolean) : []),
    [urlFilters.priority],
  );
  const selectedAreas = useMemo(
    () => (urlFilters.area ? urlFilters.area.split(',').filter(Boolean) : []),
    [urlFilters.area],
  );

  // Unified day-count for the grid + map views (SC-6371). Kept in the URL so a
  // selection survives view switches and reloads. Guard against a malformed
  // `?days=` by falling back to the default.
  const dayCount = Number(urlFilters.days) || Number(SCHEDULE_FILTER_DEFAULTS.days);

  const { data: prioritiesData } = usePlannerJobPriorities();
  const { data: categoriesData } = usePlannerJobCategories();
  const { data: areasData } = usePlannerAreas();

  // Area is filtered server-side (unlike Teams, which is applied client-side):
  // a job's area is derived from its postcode on the backend, and the filter has
  // to apply across every page of scheduled jobs, not just the loaded ones.
  const jobQueryParams = useMemo(
    () => ({
      search: searchTerm || undefined,
      priority_id: selectedPriorities.length > 0 ? selectedPriorities.map(Number) : undefined,
      job_category_id: filters.category !== 'all' ? [Number(filters.category)] : undefined,
      area_id: selectedAreas.length > 0 ? selectedAreas.map(Number) : undefined,
    }),
    [searchTerm, selectedPriorities, filters.category, selectedAreas],
  );

  const { data: unscheduledData } = useUnscheduledJobs(jobQueryParams);
  const { data: scheduledData, hasNextPage: hasMoreScheduled } = useScheduledJobsInfinite(
    jobQueryParams,
    SCHEDULED_PAGE_SIZE,
  );
  const createEvent = useCreatePlannerEvent();
  const scheduleWO = useScheduleWorkOrder();
  const unscheduleWO = useUnscheduleWorkOrder();

  const scheduledPlannerJobs = useMemo(
    () => (scheduledData?.pages ?? []).flatMap((page) => page.data),
    [scheduledData],
  );

  const jobs: Job[] = useMemo(() => {
    const unscheduled = (unscheduledData?.data ?? []).map(mapPlannerJobToJob);
    const scheduled = scheduledPlannerJobs.map(mapPlannerJobToJob);
    return [...unscheduled, ...scheduled];
  }, [unscheduledData, scheduledPlannerJobs]);

  // Scheduled-jobs auto-pagination (WRK-5235). The hook loads every page in the
  // background; a day could otherwise look empty while its jobs sit on an
  // un-loaded page. `total` comes from the first page. While more pages remain
  // (`hasMoreScheduled`) we show a passive "loading all…" notice — no user action.
  const totalScheduled = scheduledData?.pages[0]?.total ?? 0;
  const loadedScheduled = scheduledPlannerJobs.length;
  const scheduledPagination = {
    loaded: loadedScheduled,
    total: totalScheduled,
    isLoadingAll: hasMoreScheduled,
  };

  const teams: CalendarTeam[] = useMemo(() => {
    const teamMap = new Map<
      number,
      { name: string; memberCount: number; maxCapacity: number | null }
    >();
    for (const r of resources) {
      if (r.teamId && r.teamName) {
        const existing = teamMap.get(r.teamId);
        if (existing) {
          existing.memberCount++;
        } else {
          teamMap.set(r.teamId, {
            name: r.teamName,
            memberCount: 1,
            // Daily Job Capacity is a team attribute; every resource row for the
            // team carries the same value, so the first one is authoritative (SC-6668).
            maxCapacity: r.maxCapacity ?? null,
          });
        }
      }
    }
    return Array.from(teamMap.entries()).map(([id, data]) => ({
      id: String(id),
      name: data.name,
      memberCount: data.memberCount,
      maxCapacity: data.maxCapacity,
    }));
  }, [resources]);

  const invalidatePlannerJobs = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.planner.all });
    await queryClient.refetchQueries({ queryKey: queryKeys.planner.all });
  }, [queryClient]);

  const handleUpdateJob = useCallback(
    async (jobId: string, updatedJob: Partial<Job>) => {
      const { scheduledDate, teamId, startTime, endTime } = updatedJob as {
        scheduledDate?: string;
        teamId?: string;
        startTime?: string;
        endTime?: string;
      };

      // Unschedule
      if (!scheduledDate || !teamId) {
        await unscheduleWO.mutateAsync(Number(jobId));
        await invalidatePlannerJobs();
        return;
      }

      // If already scheduled (reschedule), unschedule first to clean up old records
      const existingJob = jobs.find((j) => j.id === jobId);
      if (existingJob?.scheduledDate) {
        try {
          await unscheduleWO.mutateAsync(Number(jobId));
        } catch {
          // Job might not be in Planned status yet — continue with scheduling
        }
      }

      // Schedule — use provided start/end times or default to 08:00-09:00
      const dbTeamId = Number(teamId);
      const start = startTime ?? new Date(`${scheduledDate}T08:00:00`).toISOString();
      const end = endTime ?? new Date(`${scheduledDate}T09:00:00`).toISOString();
      const member = resources.find((r) => r.teamId === dbTeamId);
      if (!member) return;

      let eventCreated = false;
      try {
        await createEvent.mutateAsync({
          userId: member.id,
          teamId: dbTeamId,
          start,
          end,
          jobId: Number(jobId),
        });
        eventCreated = true;

        await scheduleWO.mutateAsync(Number(jobId));
      } catch (err) {
        // If the event row was created but marking the WO scheduled failed,
        // the job would show up twice in scheduled lists on next refresh.
        // Roll back the event so the user can retry cleanly.
        if (eventCreated) {
          try {
            await unscheduleWO.mutateAsync(Number(jobId));
          } catch {
            // Best-effort cleanup; surface the original error.
          }
        }
        throw err;
      }

      await invalidatePlannerJobs();
    },
    [createEvent, resources, scheduleWO, unscheduleWO, invalidatePlannerJobs, jobs],
  );

  const describeFilters = useCallback(
    (values: CalendarFilterValues): string[] => {
      const parts: string[] = [];
      if (values.category !== 'all') {
        const name = categoriesData?.find((c) => String(c.id) === values.category)?.name;
        parts.push(`Category: ${name ?? values.category}`);
      }
      return parts;
    },
    [categoriesData],
  );

  const handleFiltersApply = useCallback(
    (applied: CalendarFilterValues) => {
      setUrlFilters({ category: applied.category });
      const parts = describeFilters(applied);
      if (parts.length > 0) {
        showSuccessToast('Filters applied', parts.join(', '));
      } else {
        showInfoToast('No filters to apply');
      }
    },
    [describeFilters, setUrlFilters],
  );

  const handleSelectedTeamsChange = useCallback(
    (teamIds: string[]) => {
      setUrlFilters({ teams: teamIds.join(',') });
    },
    [setUrlFilters],
  );

  // Priority is a multi-select applied instantly (like Teams), so it writes
  // straight to the URL rather than going through the Apply-filters toast flow.
  const handleSelectedPrioritiesChange = useCallback(
    (priorityIds: string[]) => {
      setUrlFilters({ priority: priorityIds.join(',') });
    },
    [setUrlFilters],
  );

  const handleSelectedAreasChange = useCallback(
    (areaIds: string[]) => {
      setUrlFilters({ area: areaIds.join(',') });
    },
    [setUrlFilters],
  );

  return (
    <CalendarView
      jobs={jobs}
      onUpdateJob={handleUpdateJob}
      onJobClick={(jobId) => navigate(`/jobs/${jobId}`)}
      teams={teams}
      search={searchTerm}
      onSearchChange={(search) => setUrlFilters({ search })}
      filters={filters}
      onFiltersApply={handleFiltersApply}
      selectedTeams={selectedTeams}
      onSelectedTeamsChange={handleSelectedTeamsChange}
      selectedPriorities={selectedPriorities}
      onSelectedPrioritiesChange={handleSelectedPrioritiesChange}
      selectedAreas={selectedAreas}
      onSelectedAreasChange={handleSelectedAreasChange}
      dayCount={dayCount}
      onDayCountChange={(days) => setUrlFilters({ days: String(days) })}
      priorities={prioritiesData ?? []}
      categories={categoriesData ?? []}
      areas={areasData ?? []}
      scheduledPagination={scheduledPagination}
    />
  );
}
